import { API_BASE, apiRequest } from "./api";
import { saveSession } from "./session";

const OAUTH_VERIFIER_KEY = "govzy_oauth_code_verifier";
const OAUTH_PROVIDERS = new Set(["google", "microsoft"]);
const OAUTH_VERIFIER_PATTERN = /^[A-Za-z0-9._~-]{43,128}$/;

function base64Url(bytes) {
  let binary = "";
  bytes.forEach(byte => { binary += String.fromCharCode(byte); });
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export async function createOAuthProof(cryptoImpl = window.crypto) {
  if (!cryptoImpl?.getRandomValues || !cryptoImpl?.subtle) {
    throw new Error("Secure sign-in is not supported by this browser.");
  }
  const randomBytes = new Uint8Array(32);
  cryptoImpl.getRandomValues(randomBytes);
  const verifier = base64Url(randomBytes);
  const verifierBytes = Uint8Array.from(verifier, character => character.charCodeAt(0));
  const digest = await cryptoImpl.subtle.digest("SHA-256", verifierBytes);
  return { verifier, challenge: base64Url(new Uint8Array(digest)) };
}

export async function beginOAuth(provider, options = {}) {
  if (!OAUTH_PROVIDERS.has(provider)) throw new Error("Unsupported sign-in provider.");
  const {
    cryptoImpl = window.crypto,
    storage = window.sessionStorage,
    navigate = destination => { window.location.href = destination; },
  } = options;
  const { verifier, challenge } = await createOAuthProof(cryptoImpl);
  storage.setItem(OAUTH_VERIFIER_KEY, verifier);
  const url = `${API_BASE}/api/auth/${provider}?${new URLSearchParams({
    code_challenge: challenge,
  })}`;
  navigate(url);
  return url;
}

export function clearOAuthProof(storage = window.sessionStorage) {
  storage.removeItem(OAUTH_VERIFIER_KEY);
}

export async function exchangeOAuthCode(code, options = {}) {
  const { storage = window.sessionStorage } = options;
  const verifier = storage.getItem(OAUTH_VERIFIER_KEY);
  if (!verifier || !OAUTH_VERIFIER_PATTERN.test(verifier)) {
    clearOAuthProof(storage);
    throw new Error("Sign-in verification is missing. Please start sign-in again.");
  }

  try {
    const response = await apiRequest("/api/auth/exchange", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code, code_verifier: verifier }),
    });
    const body = await response.json();

    if (!response.ok) {
      throw new Error(body.error || "OAuth exchange failed");
    }
    if (!body.token || !body.user) {
      throw new Error("OAuth exchange returned an invalid session");
    }

    const session = { token: body.token, user: body.user };
    saveSession(session);
    return session;
  } finally {
    clearOAuthProof(storage);
  }
}

export function createOAuthExchangeOnce(exchange = exchangeOAuthCode) {
  const exchanges = new Map();

  return code => {
    if (!exchanges.has(code)) exchanges.set(code, exchange(code));
    return exchanges.get(code);
  };
}
